import { useState, useEffect } from 'react';
import { Star, Users, Quote, CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import REFERENTIES_IMG from '../assets/images/Dgitaal_referenties.jpg';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const approachPoints = [
  'Gratis kennismakingsgesprek',
  'Duidelijke offertes zonder verrassingen',
  'Directe lijnen met onze specialisten',
  'Flexibele ondersteuning naar behoefte',
];

const sectors = [
  "KMO's",
  'Vrije beroepen',
  'Accountancy',
  'Advocatuur',
  'Bouw & Vastgoed',
  'Retail',
  'Non-profit',
  'Startups',
];

export default function Referenties() {
  const [referenties, setReferenties] = useState([]);

  useEffect(() => {
    axios.get(`${API}/referenties`).then(r => setReferenties(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="referenties-page">
      <section className="relative pt-40 pb-24 overflow-hidden" style={{ background: 'linear-gradient(135deg, #16224b 0%, #233a80 58%, #f2a100 100%)' }}>
        <div className="dot-pattern absolute inset-0" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <h1 className="text-white font-bold tracking-tight mb-4" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '-0.03em' }}>
            Onze Referenties
          </h1>
          <p className="text-white/70 max-w-xl mx-auto text-base leading-8">
            Als jong bedrijf bouwen we met trots aan onze klantrelaties.
            Ontdek wat onze eerste klanten over ons zeggen.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#16224b] font-bold mb-4" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)' }}>
            Een nieuw verhaal begint
          </h2>
          <p className="text-[#6b7184] leading-8 max-w-3xl mx-auto">
            Dgitaal is een jong en ambitieus IT-bedrijf. Hoewel we nog aan het begin staan van onze
            reis, brengen we jarenlange ervaring en frisse ideeën samen. Elke klant die we helpen,
            draagt bij aan ons groeiende succesverhaal.
          </p>
        </div>
      </section>

      <section className="py-24 bg-[#f8f9fd]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-[#16224b] font-bold mb-4" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)' }}>
              Persoonlijk en professioneel
            </h2>
            <p className="text-[#6b7184] leading-8 mb-6">
              Bij Dgitaal geloven we in een persoonlijke aanpak. We nemen de tijd om uw bedrijf en
              uitdagingen te begrijpen voordat we met oplossingen komen. Geen standaard pakketten,
              maar maatwerk dat werkt.
            </p>

            <ul className="space-y-3">
              {approachPoints.map((point) => (
                <li key={point} className="flex items-center gap-3 text-sm text-[#16224b]">
                  <CheckCircle className="w-5 h-5 text-[#3d56a6] flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg overflow-hidden shadow-2xl border border-gray-100">
            <img src={REFERENTIES_IMG} alt="Team samenwerking" className="w-full h-[460px] object-cover" />
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#16224b] font-bold mb-8" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)' }}>
            Sectoren waarin wij actief zijn
          </h2>

          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {sectors.map((sector) => (
              <span key={sector} className="px-4 py-2 rounded-full bg-[#f8f9fd] border border-gray-100 text-[#16224b] text-sm font-medium">
                {sector}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#f8f9fd]" data-testid="testimonials-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="text-[#16224b] font-bold" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)' }}>
              Klantverhalen
            </h2>
          </div>

          {referenties.length === 0 ? (
            <div className="text-center py-20">
              <Quote className="w-16 h-16 text-gray-200 mx-auto mb-4" />
              <p className="text-[#6b7184]">Referenties worden binnenkort toegevoegd.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
              {referenties.map((ref, i) => (
                <div key={ref.id || i} className="quote-card" data-testid={`referentie-${i}`}>
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}
                  </div>
                  <p className="text-[#6b7184] text-sm leading-8 italic mb-6">&ldquo;{ref.quote}&rdquo;</p>
                  <div className="flex items-center gap-4 border-t border-gray-100 pt-5">
                    <div className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-white text-lg flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #3d56a6, #f2a100)' }}>
                      {ref.contactpersoon?.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-[#16224b] text-sm">{ref.contactpersoon}</p>
                      <p className="text-[#6b7184] text-xs">{ref.functie} — {ref.bedrijf}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-white text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-[#16224b] font-bold mb-4" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
            Word ook een succesverhaal
          </h2>
          <p className="text-[#6b7184] mb-8">
            Ontdek hoe Dgitaal uw bedrijf kan helpen groeien.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-gradient">
              Neem Contact Op <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/diensten" className="btn-outline-dark">
              Bekijk Onze Diensten
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
